// ============================================================
// INPUT HANDLING
// ============================================================

import { M3_COLS, M3_ROWS, BATTLE_COLS, BATTLE_ROWS, UCFG } from './constants.ts';
import { canvas, W, H, CB, CM, ZBY, ZHY, ZMY, OXB, OYB, OXM, OYM } from './canvas.ts';
import { Game, GS } from './Game.ts';
import { Deployment } from './Deployment.ts';
import { Renderer } from './Renderer.ts';
import { showPrompt } from './prompt.ts';
import { LEADERS } from './balance.ts';

// Phase 3d Step 3: hit-test against the menu buttons drawn by Renderer.
function hitMenuButton(x: number, y: number, idx: number): boolean {
  const r = Renderer.menuButtonRect(idx);
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/** Generic rect hit-test. */
function hitRect(x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

export class Input {
  private g: Game;
  private sx = 0;
  private sy = 0;
  private dragging = false;
  /** Whether the swipe already triggered a swap during this touch */
  private swipeFired = false;
  /** Whether we're currently drag-drawing a path in deployment */
  private drawingPath = false;

  constructor(game: Game) {
    this.g = game;
    canvas.addEventListener('mousedown', (e) => this._down(e.offsetX, e.offsetY));
    canvas.addEventListener('mouseup', (e) => this._up(e.offsetX, e.offsetY));
    canvas.addEventListener('mousemove', (e) => { if (this.dragging) this._move(e.offsetX, e.offsetY); });
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.touches[0];
      const r = canvas.getBoundingClientRect();
      this._down(t.clientX - r.left, t.clientY - r.top);
    }, { passive: false });
    canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      const r = canvas.getBoundingClientRect();
      this._up(t.clientX - r.left, t.clientY - r.top);
    }, { passive: false });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (this.dragging) {
        const t = e.touches[0];
        const r = canvas.getBoundingClientRect();
        this._move(t.clientX - r.left, t.clientY - r.top);
      }
    }, { passive: false });
  }

  private _down(x: number, y: number): void {
    this.sx = x; this.sy = y;
    this.dragging = true;
    this.swipeFired = false;
    const g = this.g;
    // Surrender confirm modal — absorbs all _down (taps land on the
    // modal buttons in _up); also prevent any other handling.
    if (g.surrenderConfirmActive) {
      this.dragging = false;
      return;
    }
    // Surrender button long-press start — MATCH3 only. Must come BEFORE
    // the swipe-tracker / cell-press logic so the press is identified
    // as a surrender hold, not a stale gem tap.
    if (g.state === GS.MATCH3 && hitRect(x, y, Renderer.surrenderButtonRect())) {
      g.startSurrenderHold();
      this.dragging = false; // don't track this as a normal drag
      return;
    }
    // Story Mode — block all in-game drags / taps while dialogue overlay
    // is on screen so half-finished drags can't mutate state behind the
    // dialogue. The actual dialogue-advance happens in `_up`.
    if (g.storyController?.dialogueActive) {
      this.dragging = false; // suppress _move handling too
      return;
    }

    // --- Deployment: start drag from palette, select unit, or start path draw ---
    if (g.state === GS.DEPLOY) {
      const dep = g.deployment;
      const isPreset = dep.rules.kind === 'preset';
      // Story mode specifically locks path RE-tap (script wants the player
      // building a specific path, not undoing it). Casual / manual let
      // re-tap clear so player can re-plan freely.
      const lockPathRetap = g.gameMode === 'story';
      this.drawingPath = false;
      // Check palette area (bottom zone)
      if (y >= ZMY) {
        // Preset mode has no palette (units pre-placed by script).
        // Skip _deployPaletteDown so taps land on the preset-only
        // buttons via _up's _getDeployButtons.
        if (isPreset) {
          return;
        }
        this._deployPaletteDown(x, y, dep);
        return;
      }
      // Check battlefield grid — behavior depends on dep.pathMode
      if (y >= OYB && y < OYB + BATTLE_ROWS * CB) {
        const col = Math.floor((x - OXB) / CB);
        const row = Math.floor((y - OYB) / CB);
        if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
          const idx = dep.unitAt(col, row);
          if (dep.pathMode) {
            // PATH MODE: tap unit → select for path; tap-again on same → clear path.
            if (idx >= 0) {
              if (!lockPathRetap && dep.selectedUnit === idx) dep.clearPath();
              dep.selectedUnit = idx;
              dep.dragPos = { x, y };
              this.drawingPath = true;
            } else if (dep.selectedUnit >= 0) {
              // Tap adjacent empty cell to extend path one tile
              if (dep.addPathPoint(col, row)) this.drawingPath = true;
            }
          } else {
            // PLACE MODE: tap unit arms a grid drag (reposition or remove).
            // Preset mode: pre-placed units are LOCKED — disable drag-to-
            // remove and drag-to-reposition so the script's layout stays
            // intact. Player can still select the unit visually.
            if (idx >= 0) {
              dep.selectedUnit = idx;
              if (!isPreset) {
                dep.dragRemoveIdx = idx;
                dep.dragPos = { x, y };
              }
            }
            // Empty cell taps: leave selection alone
          }
        }
      }
      return;
    }

    // Record pressed gem for visual feedback
    if (g.state === GS.MATCH3 && g.bottomTab === 'match3' && y >= ZMY && !g.board.locked) {
      const col = Math.floor((x - OXM) / CM);
      const row = Math.floor((y - OYM) / CM);
      if (col >= 0 && col < M3_COLS && row >= 0 && row < M3_ROWS) {
        g.pressedCell = { r: row, c: col };
      }
    }

    // Targeting
    if (g.targeting && y < ZHY) {
      const col = Math.floor((x - OXB) / CB);
      const row = Math.floor((y - OYB) / CB);
      if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
        g.targetPos = { x: col, y: row };
      }
    }
  }

  /** Handle palette area press during deployment */
  private _deployPaletteDown(x: number, y: number, dep: Deployment): void {
    const palY = ZMY + 10;
    const slotH = 52;
    const palX = 10;
    const slotW = (W - 20) / dep.palette.length;

    for (let i = 0; i < dep.palette.length; i++) {
      const sx = palX + i * slotW;
      if (x >= sx && x <= sx + slotW && y >= palY && y <= palY + slotH) {
        const slot = dep.palette[i];
        if (slot.placed < slot.max) {
          dep.selectedPalette = i;
          dep.dragging = true;
          dep.dragType = slot.type;
          dep.dragPos = { x, y };
        }
        return;
      }
    }
  }

  private _move(x: number, y: number): void {
    const g = this.g;

    // Surrender long-press: if finger drifts off the button rect while
    // holding, treat as cancel (matches standard mobile UI).
    if (g.surrenderHolding) {
      if (!hitRect(x, y, Renderer.surrenderButtonRect())) {
        g.cancelSurrenderHold();
      }
      return;
    }

    // Deployment movement handler. Modes:
    //   pathMode ON  → drag from selected unit extends its path
    //   pathMode OFF → drag a placed unit; ghost follows cursor (drop in _up)
    if (g.state === GS.DEPLOY) {
      const dep = g.deployment;
      // Palette unit drag (palette → grid)
      if (dep.dragging) {
        dep.dragPos = { x, y };
        return;
      }
      // Path drawing only in path mode
      if (dep.pathMode && this.drawingPath && dep.selectedUnit >= 0 &&
          y >= OYB && y < OYB + BATTLE_ROWS * CB) {
        const col = Math.floor((x - OXB) / CB);
        const row = Math.floor((y - OYB) / CB);
        if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
          dep.addPathPoint(col, row);
        }
      }
      // Place-mode grid drag: keep tracking ghost position
      if (dep.dragRemoveIdx >= 0) {
        dep.dragPos = { x, y };
      }
      return;
    }

    // Targeting drag
    if (g.targeting && y < ZHY) {
      const col = Math.floor((x - OXB) / CB);
      const row = Math.floor((y - OYB) / CB);
      if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
        g.targetPos = { x: col, y: row };
      }
    }

    // Swipe detection during drag — fires immediately when threshold is met
    if (!this.swipeFired && g.state === GS.MATCH3 && g.bottomTab === 'match3' && this.sy >= ZMY && !g.board.locked) {
      const dx = x - this.sx, dy = y - this.sy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Lower threshold: 20% of cell size for more responsive feel
      if (dist > CM * 0.2) {
        const col = Math.floor((this.sx - OXM) / CM);
        const row = Math.floor((this.sy - OYM) / CM);
        if (col >= 0 && col < M3_COLS && row >= 0 && row < M3_ROWS) {
          let dr = 0, dc = 0;
          if (Math.abs(dx) > Math.abs(dy)) dc = dx > 0 ? 1 : -1;
          else dr = dy > 0 ? 1 : -1;
          g.handleSwap(row, col, row + dr, col + dc);
          g.selectedCell = null;
          g.pressedCell = null;
          this.swipeFired = true;
        }
      }
    }
  }

  private _up(x: number, y: number): void {
    const g = this.g;
    const dx = x - this.sx, dy = y - this.sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    this.dragging = false;
    g.pressedCell = null;

    // Surrender confirm modal — Confirm / Cancel buttons. Absorb all
    // other input until the modal is dismissed.
    if (g.surrenderConfirmActive) {
      if (hitRect(x, y, Renderer.surrenderConfirmRect())) {
        g.confirmSurrender();
      } else if (hitRect(x, y, Renderer.surrenderCancelRect())) {
        g.dismissSurrenderConfirm();
      }
      return;
    }

    // Surrender long-press release — if player let go before the 5s
    // threshold, cancel. (If they DID hold long enough, the confirm
    // modal is already up and the branch above handles taps.)
    if (g.surrenderHolding) {
      g.cancelSurrenderHold();
      return;
    }

    // Story Mode — tapping anywhere while a dialogue overlay is up
    // advances it. Intercept BEFORE any state-specific handling so the
    // tap doesn't double-fire (e.g. trigger a battlefield swap while
    // dismissing dialogue).
    if (g.storyController?.dialogueActive) {
      g.storyController.advanceDialogue();
      return;
    }

    // --- Menu tap ---
    if (g.state === GS.MENU) {
      // Session 9 follow-up — both net and non-net modes now use explicit
      // buttons for every entry point. The legacy "tap anywhere = single
      // player" affordance is gone (was easy to fire accidentally; new
      // players didn't realise other modes existed).
      //
      //   Non-net layout:  Test idx=0, Story idx=1, Casual idx=2
      //   Net layout:      Test idx=0, Create idx=1, Join idx=2,
      //                    Server idx=3, Story idx=4, Casual idx=5
      if (g.netMode) {
        if (hitMenuButton(x, y, 0)) { g.startSinglePlayer(); return; }
        if (hitMenuButton(x, y, 1)) { void g.startCreateRoom(); return; }
        if (hitMenuButton(x, y, 2)) {
          // In-app prompt — works in Electron sandbox + Capacitor WebView
          // where window.prompt() silently returns null.
          void showPrompt('Enter room code:').then((code) => {
            if (code) void g.startJoinRoom(code);
          });
          return;
        }
        // Phase 5a Step 1: idx=3 = Server URL editor.
        if (hitMenuButton(x, y, 3)) { g.promptServerUrl(); return; }
        // Story Mode at idx=4 in net layout.
        if (hitMenuButton(x, y, 4)) { g.startStoryMode(); return; }
        // Casual Mode at idx=5 in net layout.
        if (hitMenuButton(x, y, 5)) { g.startCasualMode(); return; }
        return;
      }
      // Non-net mode — explicit buttons only; missed taps do nothing.
      if (hitMenuButton(x, y, 0)) { g.startSinglePlayer(); return; }
      if (hitMenuButton(x, y, 1)) { g.startStoryMode(); return; }
      if (hitMenuButton(x, y, 2)) { g.startCasualMode(); return; }
      return;
    }

    // --- Casual level picker ---
    if (g.state === GS.CASUAL_SELECT) {
      // Back button first.
      const back = Renderer.casualBackRect();
      if (x >= back.x && x <= back.x + back.w && y >= back.y && y <= back.y + back.h) {
        g.state = GS.MENU;
        return;
      }
      const levels = g.listCasualLevels();
      for (let i = 0; i < levels.length; i++) {
        const r = Renderer.casualLevelRect(i);
        if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
          g.startCasualLevel(levels[i].id);
          return;
        }
      }
      return;
    }

    // --- Story chapter picker ---
    if (g.state === GS.STORY_SELECT) {
      // Back button first (so it doesn't get masked by an overlapping rect).
      const back = Renderer.storyBackRect();
      if (x >= back.x && x <= back.x + back.w && y >= back.y && y <= back.y + back.h) {
        g.state = GS.MENU;
        return;
      }
      const chapters = g.listChapters();
      for (let i = 0; i < chapters.length; i++) {
        const r = Renderer.storyChapterRect(i);
        if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
          g.startStoryChapter(chapters[i].id);
          return;
        }
      }
      return;
    }

    // --- Lobby tap (Cancel button) ---
    if (g.state === GS.LOBBY) {
      // Phase 3d Step 5 fix: Cancel moved to button idx 3 to avoid
      // overlap with the status message. Stay in sync with Renderer._lobby.
      if (hitMenuButton(x, y, 3)) {
        // Reset back to MENU and tear down the half-open adapter.
        try { (g.adapter as any)?.disconnect?.(); } catch { /* ignore */ }
        g.adapter = undefined as any;
        g.gameMode = 'single';
        g.myTeam = 0;
        g.roomCode = null;
        g.lobbyMessage = '';
        g.opponentSessionId = null;
        g.state = GS.MENU;
      }
      return;
    }

    // --- Deployment interactions ---
    if (g.state === GS.DEPLOY) {
      // Story Mode reuses the same _deployUp pipeline — `Deployment`'s
      // story gates (installed in Game.startDeployment) reject invalid
      // placements / path points, and `_getDeployButtons` skips the
      // save/clear/slot/remove buttons in story mode. Net effect: only
      // the scripted moves succeed.
      this._deployUp(x, y);
      return;
    }

    // Swipe in match-3 (fallback: if not fired during move)
    if (!this.swipeFired && g.state === GS.MATCH3 && g.bottomTab === 'match3' && this.sy >= ZMY && !g.board.locked) {
      if (dist > CM * 0.2) {
        const col = Math.floor((this.sx - OXM) / CM);
        const row = Math.floor((this.sy - OYM) / CM);
        if (col >= 0 && col < M3_COLS && row >= 0 && row < M3_ROWS) {
          let dr = 0, dc = 0;
          if (Math.abs(dx) > Math.abs(dy)) dc = dx > 0 ? 1 : -1;
          else dr = dy > 0 ? 1 : -1;
          g.handleSwap(row, col, row + dr, col + dc);
          g.selectedCell = null;
        }
      } else if (dist < 5) {
        // Tap select / tap-swap
        const col = Math.floor((x - OXM) / CM);
        const row = Math.floor((y - OYM) / CM);
        if (col >= 0 && col < M3_COLS && row >= 0 && row < M3_ROWS) {
          if (g.selectedCell) {
            const dr = Math.abs(g.selectedCell.r - row);
            const dc = Math.abs(g.selectedCell.c - col);
            if (dr + dc === 1) {
              g.handleSwap(g.selectedCell.r, g.selectedCell.c, row, col);
              g.selectedCell = null;
            } else {
              // Tapped a non-adjacent cell — reselect
              g.selectedCell = { r: row, c: col };
            }
          } else {
            g.selectedCell = { r: row, c: col };
          }
        }
      }
    }

    // Targeting confirm — Session 8 fix: only commit if the lift
    // actually happened on the battlefield. Earlier this fired any
    // time `g.targetPos` existed, which meant tapping the Back
    // button (in the SCHED panel below ZHY) after a stale targetPos
    // would silently confirm the spell and rob the user of their
    // back-out. The `_down` / `_move` handlers above already gate
    // targetPos updates on `y < ZHY`, so this matching gate keeps
    // confirm consistent with where the user actually clicked.
    if (g.targeting && g.targetPos && y < ZHY) {
      g.confirmTarget(g.targetPos.x, g.targetPos.y);
      return;
    }

    // Battle-phase tab strip click — toggle bottom tab.
    if ((g.state === GS.MATCH3 || g.state === GS.SCHED || g.state === GS.EXEC) &&
        y >= ZMY && y <= ZMY + 22) {
      g.setBottomTab(x < W / 2 ? 'match3' : 'sched');
      return;
    }

    // Scheduling buttons — coords mirror Renderer._scheduling (Session 6c
    // follow-up redesign). Layout:
    //   tabH = 22; zy = ZMY + tabH + 4 = ZMY + 26
    //   slot row at sy = zy + 2 = ZMY + 28, height sh = 50
    //   slotsBottom = sy + sh = ZMY + 78
    //   bodyTop = slotsBottom + 12 = ZMY + 90
    if (g.state === GS.SCHED && g.bottomTab === 'sched' && y >= ZMY + 22) {
      const slotsBottom = ZMY + 78;
      const bodyTop = slotsBottom + 12;

      if (g.schedStep === 'target') {
        // Back button below target prompt.
        const bw = W * 0.28, bh = 30, bx = (W - bw) / 2, by = bodyTop + 40;
        if (y >= by && y <= by + bh && x >= bx && x <= bx + bw) {
          g.goBackSlot();
          return;
        }
        return; // taps to battlefield handled elsewhere
      }

      if (g.schedStep === 'confirm') {
        // Back + Confirm side-by-side.
        const rowY = bodyTop + 8;
        const rowH = 48;
        const padX = W * 0.05;
        const totalW = W - padX * 2;
        const gapX = 10;
        const backW = totalW * 0.30;
        const confirmW = totalW - backW - gapX;
        // Back.
        if (y >= rowY && y <= rowY + rowH && x >= padX && x <= padX + backW) {
          g.goBackSlot();
          return;
        }
        // Confirm.
        const cx = padX + backW + gapX;
        if (y >= rowY && y <= rowY + rowH && x >= cx && x <= cx + confirmW) {
          g.confirmSchedule();
          return;
        }
        return;
      }

      // 'slot' step — Back (top-right) if undoable + spell list + Pass.
      if (g.schedStep === 'slot' && !g.targeting) {
        let listY = bodyTop;
        if (g.spellSlot > 0) {
          const bw = W * 0.24, bh = 28, bx = W - W * 0.05 - bw, by = bodyTop;
          if (y >= by && y <= by + bh && x >= bx && x <= bx + bw) {
            g.goBackSlot();
            return;
          }
          listY = bodyTop + bh + 10;
        }
        let by = listY;
        for (let i = 0; i < g.availSpells.length; i++) {
          if (y >= by && y <= by + 36 && x >= W * 0.08 && x <= W * 0.92) {
            g.selectSpell(g.availSpells[i].el); return;
          }
          by += 42;
        }
        if (y >= by && y <= by + 34 && x >= W * 0.08 && x <= W * 0.92) {
          g.skipSlot();
        }
      }
    }

    // Game over restart
    if (g.state === GS.OVER) {
      // Story Mode: suppress the button while post / lose dialogue is on
      // screen (the dialogue-advance branch at the top of _up handles
      // those taps). Once the dialogue is done:
      //   - story + lost → retry chapter
      //   - story + won  → back to chapter list (STORY_SELECT)
      //   - casual + lost → retry level
      //   - casual + won  → back to casual level list
      //   - other        → restart to MENU
      if (g.storyController?.dialogueActive) return;
      const cy = ZMY + (H - ZMY) / 2;
      if (y >= cy + 30 && y <= cy + 74 && x >= W * 0.25 && x <= W * 0.75) {
        const lost = g.winner !== g.myTeam && g.winner !== -1;
        if (g.storyController && lost) {
          g.retryStoryChapter();
        } else if (g.storyController) {
          g.exitToStorySelect();
        } else if (g.gameMode === 'casual' && lost) {
          g.retryCasualLevel();
        } else if (g.gameMode === 'casual') {
          g.exitToCasualSelect();
        } else {
          g.restart();
        }
      }
    }
  }

  /** Handle touch/click release during deployment */
  private _deployUp(x: number, y: number): void {
    const g = this.g;
    const dep = g.deployment;

    // Stop path drag-drawing
    this.drawingPath = false;

    // --- Place-mode grid drag: drop in bottom = remove, on cell = reposition ---
    if (dep.dragRemoveIdx >= 0) {
      const idx = dep.dragRemoveIdx;
      dep.dragRemoveIdx = -1;
      dep.dragPos = null;
      if (y >= ZMY) {
        dep.removeUnit(idx);
        dep.selectedUnit = -1;
      } else if (y >= OYB && y < OYB + BATTLE_ROWS * CB) {
        const col = Math.floor((x - OXB) / CB);
        const row = Math.floor((y - OYB) / CB);
        if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
          // moveUnit() validates origin / occupancy / player zone internally
          dep.moveUnit(idx, col, row);
        }
      }
      // Otherwise: released outside both zones → cancel (unit stays put)
      return;
    }

    // --- Drop dragged unit onto grid ---
    if (dep.dragging && dep.dragType) {
      dep.dragging = false;
      const col = Math.floor((x - OXB) / CB);
      const row = Math.floor((y - OYB) / CB);
      if (col >= 0 && col < BATTLE_COLS && row >= 0 && row < BATTLE_ROWS) {
        dep.placeUnit(dep.dragType, col, row);
      }
      dep.dragType = '';
      dep.dragPos = null;
      dep.selectedPalette = -1;
      return;
    }

    // --- Bottom zone button checks ---
    if (y >= ZMY) {
      const btnZone = this._getDeployButtons(dep);
      for (const btn of btnZone) {
        if (x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h) {
          btn.action();
          return;
        }
      }
      return;
    }

    // Grid tap (unified): selection + path edits are handled entirely
    // via _down + _move. _up has nothing left to do for the grid itself.
  }

  /**
   * Get deployment button layout for hit testing.
   * Row A:  [Draw Paths toggle (40%)] [Confirm / placeholder (60%)]
   * Row B:  [Slot 1] [Slot 2] [Slot 3]
   * Row C:  [↓ Save] [🗑 Clear]
   * Row D:  [Remove selected unit]  (only when a unit is selected)
   */
  _getDeployButtons(dep: Deployment): { x: number; y: number; w: number; h: number; action: () => void }[] {
    const g = this.g;
    const buttons: { x: number; y: number; w: number; h: number; action: () => void }[] = [];
    const btnH = 38;
    // Session 9 — Row A pushed down by leader-picker row (22px tall +
    // 8px gap). Single mode adds an extra AI-leader row (+26px more).
    // Both numbers must mirror Renderer._deployPanel exactly.
    const showAiPicker = g.gameMode === 'single';
    const baseY = showAiPicker ? ZMY + 122 : ZMY + 96;

    // --- Row L (Session 9): Leader picker hit-rects. Manual mode only —
    //     preset modes hide the picker (script chooses the leader).
    //     Single mode adds a second row for the AI's leader. ---
    if (dep.rules.kind === 'manual') {
      const leaderY = ZMY + 66;
      const leaderH = 22;
      const aiLeaderY = leaderY + leaderH + 4;
      const leaderIds = Object.keys(LEADERS);
      const ldrGap = 4;
      const ldrW = (W - W * 0.08 - ldrGap * (leaderIds.length - 1)) / leaderIds.length;
      for (let i = 0; i < leaderIds.length; i++) {
        const id = leaderIds[i];
        const lx = W * 0.04 + i * (ldrW + ldrGap);
        // Player's own row — always present.
        buttons.push({
          x: lx, y: leaderY, w: ldrW, h: leaderH,
          action: () => { dep.setLeader(id); g.slotAction = 'none'; },
        });
        // AI row — single mode only.
        if (showAiPicker) {
          buttons.push({
            x: lx, y: aiLeaderY, w: ldrW, h: leaderH,
            action: () => { dep.setAiLeader(id); g.slotAction = 'none'; },
          });
        }
      }
    }

    // --- Row A left: Draw Paths toggle (always present) ---
    const dpW = W * 0.36;
    const cfX = W * 0.04 + dpW + 8;
    const cfW = W - cfX - W * 0.04;
    buttons.push({
      x: W * 0.04, y: baseY, w: dpW, h: btnH,
      action: () => { dep.togglePathMode(); g.slotAction = 'none'; },
    });

    // --- Row A right: Confirm / Start Battle (only when canConfirm) ---
    // Story Mode (preset rules with active storyController): don't
    // register the hit-rect while the script gates Start Battle (e.g.
    // before expectStartBattle fires). Renderer's preset panel always
    // shows it active; this prevents the tap from silently running
    // startBattle that would just be rejected by the controller.
    const storyBlocksStart = g.gameMode === 'story' && g.storyController
      && !g.storyController.isStartBattleAllowed();
    if (dep.canConfirm() && !storyBlocksStart) {
      buttons.push({
        x: cfX, y: baseY, w: cfW, h: btnH,
        action: () => { g.slotAction = 'none'; g.startBattle(); },
      });
    }

    // Preset mode (Story / Casual): skip Rows B/C/D entirely (no palette
    // → no save slots, no save/clear toggles, no remove button since
    // units are locked). Provide ONLY the Back button at the bottom —
    // its geometry MUST match `_deployPanelPreset` exactly.
    if (dep.rules.kind === 'preset') {
      const backY = baseY + btnH + 18;
      const backH = 32;
      buttons.push({
        x: W * 0.20, y: backY, w: W * 0.60, h: backH,
        action: () => {
          // Casual → casual select; Story → MENU (no story-select
          // mid-chapter back path defined elsewhere either).
          if (g.gameMode === 'casual') g.exitToCasualSelect();
          else g.restart();
        },
      });
      return buttons;
    }

    // ----- Manual mode (single / PvP) — full panel -----

    // --- Row B: Save/Load slot row (3 tiles) ---
    const slotY = baseY + btnH + 8;             // ZMY + 118
    const slotH = 30;
    const slotGap = 4;
    const slotW = (W * 0.92 - slotGap * 2) / 3;
    for (let i = 0; i < 3; i++) {
      const sx = W * 0.04 + i * (slotW + slotGap);
      buttons.push({
        x: sx, y: slotY, w: slotW, h: slotH,
        action: () => { g.triggerSlot(i); },
      });
    }

    // --- Row C: Save / Clear toggle ---
    const actY = slotY + slotH + 6;             // ZMY + 154
    const actH = 30;
    const actW = W * 0.44;
    buttons.push({
      x: W * 0.04, y: actY, w: actW, h: actH,
      action: () => { g.toggleSlotAction('save'); },
    });
    buttons.push({
      x: W * 0.52, y: actY, w: actW, h: actH,
      action: () => { g.toggleSlotAction('clear'); },
    });

    // --- Row D: Remove selected unit (conditional) ---
    if (dep.selectedUnit >= 0) {
      const removeY = actY + actH + 8;          // ZMY + 192
      buttons.push({
        x: W * 0.04, y: removeY, w: W * 0.92, h: btnH,
        action: () => {
          dep.removeUnit(dep.selectedUnit);
          dep.selectedUnit = -1;
        },
      });
    }
    // --- Row E: Back to Main Menu (single only — PvP has LOBBY cancel) ---
    if (g.gameMode === 'single') {
      const backY = dep.selectedUnit >= 0
        ? actY + actH + 8 + btnH + 8
        : actY + actH + 8;
      const backH = 30;
      buttons.push({
        x: W * 0.25, y: backY, w: W * 0.50, h: backH,
        action: () => { g.restart(); },
      });
    }

    return buttons;
  }
}
